import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewEncapsulation } from '@angular/core';
import { MapIconsComponent } from '@curbnturf/custom-svgs/src/lib/map-icons/map-icons.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { toString } from '../util/util';
import { NgbHighlight } from './highlight';

/**
 * The context for the typeahead result template in case you want to override the default one.
 */
export interface ResultTemplateContext {
  /**
   * Your typeahead result item.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;

  /**
   * Search term from the `<input>` used to get current result.
   */
  term: string;

  /**
   * The formatter function passed through to the outlet context so a custom
   * resultTemplate can call it the same way the default #rt template does.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any) => string;
}

@Component({
  selector: 'ngb-typeahead-window',
  exportAs: 'ngbTypeaheadWindow',
  encapsulation: ViewEncapsulation.None,
  host: {
    '(mousedown)': '$event.preventDefault()',
    class: 'dropdown-menu-cnt show',
    role: 'listbox',
    '[id]': 'id',
  },
  template: `
    <ng-template #rt let-result="result" let-term="term" let-formatter="formatter">
      <ngb-highlight [result]="formatter(result)" [term]="term"> </ngb-highlight>
    </ng-template>
    @for (result of results; track result; let idx = $index) {
      <button
        type="button"
        class="dropdown-item-cnt"
        role="option"
        [id]="id + '-' + idx"
        [class.active]="idx === activeIdx"
        (mouseenter)="markActive(idx)"
        (click)="select(result)"
        [class.boondock]="result.type === 'boondock'"
        [class.standard]="result.type === 'standard'"
      >
        @if (result.type === 'standard') {
          <span class="dropdown-icons-standard">
            <curbnturf-map-icons
              type="pricePin"
              [string]="(result?.price | currency: 'USD' : '$' : '1.0-0') ?? ''"
            >
            </curbnturf-map-icons>
          </span>
        }
        @if (result.type === 'boondock') {
          <span class="dropdown-icons">
            <curbnturf-misc-svg icon="mapPin" color="#465d50"> </curbnturf-misc-svg>
          </span>
        }
        <ng-template
          [ngTemplateOutlet]="resultTemplate || rt"
          [ngTemplateOutletContext]="{
            result: result,
            term: term,
            formatter: formatter
          }"
        >
        </ng-template>
      </button>
    }
  `,
  styles: ['.dropdown-item-cnt { display: flex; }'],
  standalone: true,
  imports: [NgbHighlight, MapIconsComponent, MiscSvgComponent, NgTemplateOutlet, CurrencyPipe],
})
export class NgbTypeaheadWindow implements OnInit {
  activeIdx = 0;

  /**
   *  The id for the typeahead window. The id should be unique and the same
   *  as the associated typeahead's id.
   */
  @Input() id: string;

  /**
   * Flag indicating if the first row should be active initially
   */
  @Input() focusFirst = true;

  /**
   * Typeahead match results to be displayed
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() results: any;

  /**
   * Search term used to get current results
   */
  @Input() term: string;

  /**
   * A function used to format a given result before display. This function should return a formatted string without any
   * HTML markup
   */
  @Input() formatter = toString;

  /**
   * A template to override a matching result default display
   */
  @Input() resultTemplate: TemplateRef<ResultTemplateContext>;

  /**
   * Event raised when user selects a particular result row
   */
  @Output('select') selectEvent = new EventEmitter();

  @Output('activeChange') activeChangeEvent = new EventEmitter();

  hasActive() {
    return this.activeIdx > -1 && this.activeIdx < this.results.length;
  }

  getActive() {
    return this.results[this.activeIdx];
  }

  markActive(activeIdx: number) {
    this.activeIdx = activeIdx;
    this._activeChanged();
  }

  next() {
    if (this.activeIdx === this.results.length - 1) {
      this.activeIdx = this.focusFirst ? (this.activeIdx + 1) % this.results.length : -1;
    } else {
      this.activeIdx++;
    }
    this._activeChanged();
  }

  prev() {
    if (this.activeIdx < 0) {
      this.activeIdx = this.results.length - 1;
    } else if (this.activeIdx === 0) {
      this.activeIdx = this.focusFirst ? this.results.length - 1 : -1;
    } else {
      this.activeIdx--;
    }
    this._activeChanged();
  }

  resetActive() {
    this.activeIdx = this.focusFirst ? 0 : -1;
    this._activeChanged();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  select(item: any) {
    this.selectEvent.emit(item);
  }

  ngOnInit() {
    this.resetActive();
  }

  private _activeChanged() {
    this.activeChangeEvent.emit(this.activeIdx >= 0 ? this.id + '-' + this.activeIdx : undefined);
  }
}
